/**
 * Composable for managing password-protected content authorization.
 * Handles token storage, authorization requests, and token validation.
 */

import { useLocalStorage } from '@vueuse/core'

import { AUTHORISED_CONTENT_ENABLED } from '../util/features'

const STORAGE_KEY = 'digistorm:gate-tokens'
const GATE_TOKENS_STATE_KEY = 'authorised-content:gate-tokens'
const LAST_AUTHORIZED_GATE_STATE_KEY = 'authorised-content:last-authorized-gate'
const LAST_CLEARED_GATE_STATE_KEY = 'authorised-content:last-cleared-gate'

interface ApiError {
    message?: string
    data?: {
        message?: string
    }
}

interface AuthorisedContent {
    isProtected: boolean
    isAuthorized: boolean
    requiresToken: boolean
    gateUid: string | null
    gateType: string | null
    gateName: string | null
    message: string | null
    enableLogoutButton: boolean
}

interface AuthoriseResult {
    success: boolean
    token: string | null
    message: string | null
}

interface ValidateTokenResult {
    valid: boolean
}

interface GateTokens {
    [gateUid: string]: string
}

interface GateAuthorizationEvent {
    gateUid: string
    timestamp: number
}

let gateTokenStorage: Ref<GateTokens> | null = null
let gateTokenStorageSyncInitialized = false

const cloneGateTokens = (tokens: GateTokens | null | undefined): GateTokens => ({ ...(tokens ?? {}) })

const areGateTokensEqual = (left: GateTokens, right: GateTokens): boolean => {
    const leftKeys = Object.keys(left)
    const rightKeys = Object.keys(right)

    if (leftKeys.length !== rightKeys.length) {
        return false
    }

    return leftKeys.every((key) => left[key] === right[key])
}

const getGateTokenStorage = (): Ref<GateTokens> | null => {
    if (!import.meta.client) {
        return null
    }

    gateTokenStorage ??= useLocalStorage<GateTokens>(STORAGE_KEY, {})

    return gateTokenStorage
}

const persistGateTokens = (tokens: GateTokens): GateTokens => {
    const nextTokens = cloneGateTokens(tokens)
    const storage = getGateTokenStorage()

    if (storage) {
        storage.value = nextTokens
    }

    return nextTokens
}

const useGateTokensState = () => useState<GateTokens>(GATE_TOKENS_STATE_KEY, () => getStoredGateTokens())

const useLastAuthorizedGateState = () =>
    useState<GateAuthorizationEvent | null>(LAST_AUTHORIZED_GATE_STATE_KEY, () => null)

const useLastClearedGateState = () => useState<string | null>(LAST_CLEARED_GATE_STATE_KEY, () => null)

const ensureGateTokenStorageSync = (gateTokens: Ref<GateTokens>, lastClearedGate: Ref<string | null>) => {
    if (!import.meta.client || gateTokenStorageSyncInitialized) {
        return
    }

    const storage = getGateTokenStorage()
    if (!storage) {
        return
    }

    gateTokenStorageSyncInitialized = true

    watch(
        storage,
        (tokens) => {
            const nextTokens = cloneGateTokens(tokens)

            if (areGateTokensEqual(gateTokens.value, nextTokens)) {
                return
            }

            gateTokens.value = nextTokens
            lastClearedGate.value = Date.now().toString()
        },
        {
            deep: true,
            immediate: true,
        },
    )
}

/**
 * Global reactive ref for broadcasting gate authorization events.
 * Components can watch this to know when a gate has been authorized.
 */
export function useGateAuthorizationEvents() {
    const lastAuthorizedGate = useLastAuthorizedGateState()

    return {
        lastAuthorizedGate: readonly(lastAuthorizedGate),
    }
}

/**
 * Get all stored gate tokens from the shared local storage ref.
 * Safe for SSR - returns empty object on server
 */
export function getStoredGateTokens(): GateTokens {
    return cloneGateTokens(getGateTokenStorage()?.value)
}

export function setStoredGateToken(gateUid: string, token: string): GateTokens {
    return persistGateTokens({
        ...getStoredGateTokens(),
        [gateUid]: token,
    })
}

/**
 * Composable for managing protected content authorization
 */
export function useProtectedContent() {
    const { cmsRequest } = useGraphQL()

    const isAuthorizing = ref(false)
    const isLoggingOut = ref(false)
    const authError = ref<string | null>(null)
    const gateTokens = useGateTokensState()
    const lastAuthorizedGate = useLastAuthorizedGateState()
    const lastClearedGate = useLastClearedGateState()

    ensureGateTokenStorageSync(gateTokens, lastClearedGate)

    /**
     * Persist tokens to localStorage
     */
    const persistTokens = () => {
        persistGateTokens(gateTokens.value)
    }

    const getErrorMessage = (error: unknown, fallback: string): string => {
        if (!error || typeof error !== 'object') {
            return fallback
        }

        const apiError = error as ApiError

        return apiError.data?.message || apiError.message || fallback
    }

    const removeToken = (gateUid: string): void => {
        const { [gateUid]: _, ...rest } = gateTokens.value
        gateTokens.value = rest
        persistTokens()
        lastClearedGate.value = gateUid
    }

    /**
     * Check if content requires authorization
     */
    const requiresAuth = (authorisedContent: AuthorisedContent | null | undefined): boolean => {
        if (!AUTHORISED_CONTENT_ENABLED) return false
        if (!authorisedContent) return false
        return authorisedContent.isProtected && !authorisedContent.isAuthorized
    }

    /**
     * Get token for a specific gate UID
     */
    const getToken = (gateUid: string): string | null => {
        return gateTokens.value[gateUid] || null
    }

    /**
     * Get all stored tokens
     */
    const getAllTokens = (): GateTokens => {
        return { ...gateTokens.value }
    }

    /**
     * Get comma-separated token string for X-Gate-Token header
     */
    const getTokenHeader = (): string | null => {
        const tokens = Object.values(gateTokens.value)
        return tokens.length > 0 ? tokens.join(',') : null
    }

    /**
     * Check if we have any stored tokens
     */
    const hasStoredToken = computed(() => Object.keys(gateTokens.value).length > 0)

    /**
     * Check if we have a token for a specific gate
     */
    const hasTokenForGate = (gateUid: string): boolean => {
        return !!gateTokens.value[gateUid]
    }

    /**
     * Submit password to authorize against a gate
     */
    const authorise = async (gateUid: string, password: string): Promise<boolean> => {
        isAuthorizing.value = true
        authError.value = null

        try {
            const result = await cmsRequest<AuthoriseResult>({
                uri: 'authorised-content/password-authorise',
                method: 'POST',
                body: {
                    gateUid,
                    password,
                },
            })

            if (result?.success && result.token) {
                gateTokens.value[gateUid] = result.token
                persistTokens()

                lastAuthorizedGate.value = { gateUid, timestamp: Date.now() }

                return true
            }

            authError.value = result?.message || 'Authorization failed'
            return false
        } catch (error) {
            authError.value = getErrorMessage(error, 'Authorization failed')
            return false
        } finally {
            isAuthorizing.value = false
        }
    }

    /**
     * Validate a stored token against the backend
     */
    const validateToken = async (gateUid: string): Promise<boolean> => {
        const token = getToken(gateUid)
        if (!token) return false

        try {
            const result = await cmsRequest<ValidateTokenResult>({
                uri: 'authorised-content/validate-token',
                method: 'POST',
                body: {
                    gateUid,
                    token,
                },
            })

            if (!result?.valid) {
                removeToken(gateUid)
                return false
            }

            return true
        } catch {
            removeToken(gateUid)
            return false
        }
    }

    /**
     * Validate all stored tokens and remove invalid ones
     */
    const validateAllTokens = async (): Promise<void> => {
        const handles = Object.keys(gateTokens.value)
        if (handles.length === 0) return

        const validationPromises = handles.map(async (handle) => {
            const isValid = await validateToken(handle)
            return { handle, isValid }
        })

        await Promise.all(validationPromises)
    }

    /**
     * Clear gate tokens based on mode
     */
    const clearTokens = (mode: 'all-saml' | 'all-gates') => {
        const tokens = gateTokens.value

        switch (mode) {
            case 'all-saml':
                // Remove all SAML gate tokens - rebuild object without saml/ keys
                gateTokens.value = Object.fromEntries(
                    Object.entries(tokens).filter(([gateUid]) => !gateUid.startsWith('saml/')),
                )
                break

            case 'all-gates':
                // Clear everything
                gateTokens.value = {}
                break
        }

        persistTokens()
        lastClearedGate.value = mode
    }

    /**
     * Clear authorization for a specific gate or all gates
     */
    const clearAuth = async (gateUid?: string): Promise<void> => {
        try {
            isLoggingOut.value = true
            if (gateUid) {
                const token = gateTokens.value[gateUid]
                if (token) {
                    try {
                        await cmsRequest({
                            uri: 'authorised-content/logout',
                            method: 'POST',
                            body: {
                                gateUid,
                                token,
                            },
                        })
                    } catch {
                        // Ignore logout errors and still clear locally.
                    }
                }
                removeToken(gateUid)
            } else {
                const gateTokenEntries = Object.entries(gateTokens.value)
                await Promise.all(
                    gateTokenEntries.map(([storedGateUid, token]) =>
                        cmsRequest({
                            uri: 'authorised-content/logout',
                            method: 'POST',
                            body: {
                                gateUid: storedGateUid,
                                token,
                            },
                        }).catch(() => {
                            /* Ignore errors */
                        }),
                    ),
                )
                gateTokens.value = {}
                lastClearedGate.value = 'all-gates'
                persistTokens()
                return
            }
            persistTokens()
        } finally {
            isLoggingOut.value = false
        }
    }

    return {
        // State
        isAuthorizing,
        isLoggingOut: readonly(isLoggingOut),
        authError,
        gateTokens: readonly(gateTokens),
        hasStoredToken,
        lastClearedGate: readonly(lastClearedGate),

        // Methods
        requiresAuth,
        getToken,
        getAllTokens,
        getTokenHeader,
        hasTokenForGate,
        authorise,
        validateToken,
        validateAllTokens,
        removeToken,
        clearAuth,
        clearTokens,
    }
}
